import { SavingsBreakdown, SimulationWarning } from '../../../playground/endpoints/_shared';
import { simulateV2Product } from '../../api';
import { LifeMaxFormValues } from './schema';

export const SLUG = 'life-max-insurance';

// `output/blocks.py::build_coverages_block` -- Life Max's own shape
// (`before_tax`/`after_tax`), always zeroed, unlike the shared `Coverage`
// type's `net_premium`/`gross_premium`.
export interface LifeMaxCoverage {
  insured_capital: number;
  before_tax: number;
  tax_rate: number;
  after_tax: number;
}

export interface LifeMaxPremiumBreakdown {
  basic: number;
  agreed: number;
  excess: number;
}

export interface LifeMaxInitialContribution {
  total: number;
  basic: number;
  excess: number;
}

export interface LifeMaxYearlyRow {
  year: number;
  age: number;
  actuarial_age: number;
  accumulated_prime: number;
  policy_value: number;
  death_compensation: number;
  coverage_costs: number;
  policy_expenses: number;
  rentability: number;
  withdrawable_amount?: number;
  [key: string]: number | undefined;
}

export interface LifeMaxMonthlyRow {
  month: number;
  calendar_month: number;
  year: number;
  age: number;
  actuarial_age: number;
  policy_value: number;
  rentability: number;
  net_contribution: number;
  cost: number;
  agreed_premium: number;
  accumulated_prime: number;
  coverage_costs: number;
  coverage_costs_breakdown?: Record<string, number>;
  policy_expenses: number;
  death_compensation: number;
  withdrawable_amount: number;
}

export interface LifeMaxResponse {
  uf_value: number;
  currency: string;
  timeframe_months: number;
  premium: LifeMaxPremiumBreakdown;
  coverages: { death?: LifeMaxCoverage };
  savings: SavingsBreakdown;
  initial_contribution: LifeMaxInitialContribution;
  projection_by_year: LifeMaxYearlyRow[];
  projection_by_month?: LifeMaxMonthlyRow[];
  warnings: SimulationWarning[];
}

// The rate fields as `LifeMaxInput` wants them -- NOT `_shared`'s
// `buildRatePayload`: `rate_mode` must travel, v2's stepped mode is named
// `tiered`, and the schedule rides as a real JSON array (this route posts a
// JSON body, unlike v1's query params).
function buildRatePayload(values: LifeMaxFormValues) {
  return values.rate_mode === 'fixed'
    ? { rate_mode: 'fixed', projection_rate: values.projection_rate }
    : {
      rate_mode: 'tiered',
      projection_rate_schedule: values.projection_rate_schedule ?? [],
    };
}

export function buildLifeMaxPayload(values: LifeMaxFormValues) {
  const {
    rate_mode: _rateMode,
    projection_rate: _projectionRate,
    projection_rate_schedule: _projectionRateSchedule,
    ...rest
  } = values;
  return {
    ...rest,
    ...buildRatePayload(values),
    // Life Max only supports compensation plan B
    // (`validators/common.assert_compensation_plan_b`), so there is no field
    // for it -- it is pinned here to keep the request self-describing.
    compensation_plan: 'B',
    // Asks the engine for `projection_by_month`, which the monthly table and
    // chart read.
    debug: true,
  };
}

export async function simulateLifeMax(
  values: LifeMaxFormValues,
): Promise<LifeMaxResponse> {
  return simulateV2Product<LifeMaxResponse>(SLUG, buildLifeMaxPayload(values));
}
